import type { FeatureSource, FeatureSourceFieldMapping, MultiValueFields } from '../models/control-form';
import type { MapFeatureLink } from '../models/map-feature-link';

/**
 * `feature_source` (control_form.ui_config): prefill header fields from the map feature the user links
 * in the header Location picker. Pure data in, values out — mobile evaluates the same rules offline, so
 * keep this free of Angular, the store and ArcGIS.
 */

/** The header field keys the evaluator reads (`ui_config.header.fields[]`). */
export interface FeatureSourceHeaderField {
  name: string;
  multi_select?: boolean;
  ui_config?: { pick_list_values?: unknown[] | null } | null;
}

/** A project bid item as the form already offers it for `_bid_item`: `item` is the number, `value` what the form stores. */
export interface FeatureSourceBidItem {
  item?: string | number | null;
  value: unknown;
}

export interface FeatureSourceContext {
  bidItems?: FeatureSourceBidItem[] | null;
}

/**
 * The values of a property listed in `multi_value_fields` (split on its separator, trimmed, empty parts dropped); a
 * property not listed is one value. Blank or object values give [].
 */
export function splitValues(properties: Record<string, unknown> | null | undefined, field: string,
  multiValueFields?: MultiValueFields | null): string[] {
  const raw = properties?.[field];
  if (raw === null || raw === undefined || typeof raw === 'object') return [];
  const separator = multiValueFields && Object.prototype.hasOwnProperty.call(multiValueFields, field) ? multiValueFields[field] : null;
  const parts = typeof separator === 'string' && separator ? String(raw).split(separator) : [String(raw)];
  return parts.map(part => part.trim()).filter(part => part !== '');
}

/** Special header field resolved against the project's bid items rather than pick_list_values. */
export const BID_ITEM_FIELD = '_bid_item';

/**
 * Header field name -> value to set, for the fields that resolved (spec rules 1-7). A mapping that does
 * not resolve is left out, so a prefill never blanks a field.
 */
export function resolveFeatureSourcePrefill(
  featureSource: FeatureSource | null | undefined,
  properties: Record<string, unknown> | null | undefined,
  headerFields: FeatureSourceHeaderField[] | null | undefined,
  ctx: FeatureSourceContext = {},
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const mappings = featureSource?.fields;
  if (!mappings || typeof mappings !== 'object' || !properties || typeof properties !== 'object') return result;
  const fieldsByName = new Map((headerFields || []).filter(field => field?.name).map(field => [field.name, field]));
  for (const [target, mapping] of Object.entries(mappings)) {
    // 1. Only header fields.
    const field = fieldsByName.get(target);
    const spec = normalizeMapping(mapping);
    if (!field || !spec) continue;
    // 2. Never blank a field.
    let raw = properties[spec.field];
    if (isBlank(raw) || typeof raw === 'object') continue;
    // Multi-value property: the form's own `where` value for it, else its first value.
    if (isMultiValue(featureSource.multi_value_fields, spec.field)) {
      const values = splitValues(properties, spec.field, featureSource.multi_value_fields);
      const wanted = featureSource.where?.[spec.field];
      const chosen = wanted !== undefined && wanted !== null && values.includes(String(wanted).trim()) ? String(wanted).trim() : values[0];
      if (chosen === undefined) continue;
      raw = chosen;
    }
    // 3. Optional value map.
    const key = String(raw);
    const mapped = spec.values && Object.prototype.hasOwnProperty.call(spec.values, key) ? spec.values[key] : raw;
    if (isBlank(mapped)) continue;
    let value: unknown;
    if (target === BID_ITEM_FIELD) {
      // 5. Against the project's bid items; stores what the form stores for _bid_item.
      const bidItem = matchBidItem(mapped, ctx.bidItems);
      if (!bidItem) continue;
      value = bidItem.value;
    } else if (Array.isArray(field.ui_config?.pick_list_values) && field.ui_config.pick_list_values.length) {
      // 4. A pick list keeps only its own options.
      const option = matchOption(mapped, field.ui_config.pick_list_values);
      if (option === undefined) continue;
      value = option;
    } else {
      // 7. Plain fields take the value as is.
      value = mapped;
    }
    // 6. Multi-select controls hold arrays.
    result[target] = field.multi_select ? [value] : value;
  }
  return result;
}

/**
 * `where`: every key/value must equal the feature's property (trimmed string compare); for a `multi_value_fields`
 * property, any of its values. No `where` matches everything.
 */
export function featureMatchesWhere(
  where: FeatureSource['where'] | null | undefined,
  properties: Record<string, unknown> | null | undefined,
  multiValueFields?: MultiValueFields | null,
): boolean {
  if (!where || typeof where !== 'object') return true;
  return Object.entries(where).every(([key, expected]) => {
    const wanted = String(expected ?? '').trim();
    if (isMultiValue(multiValueFields, key)) return splitValues(properties, key, multiValueFields).includes(wanted);
    const actual = properties?.[key];
    if (actual === null || actual === undefined) return false;
    return String(actual).trim() === wanted;
  });
}

function isMultiValue(multiValueFields: MultiValueFields | null | undefined, field: string): boolean {
  return !!multiValueFields && Object.prototype.hasOwnProperty.call(multiValueFields, field)
    && typeof multiValueFields[field] === 'string' && !!multiValueFields[field];
}

/** True when a link points at the feature_source layer (the only links that prefill). */
export function featureSourceMatchesLink(featureSource: FeatureSource | null | undefined, link: MapFeatureLink | null | undefined): boolean {
  return !!featureSource?.layer && !!link?.map_feature_ref?.layerId && link.map_feature_ref.layerId === featureSource.layer;
}

/** The layer the picker is restricted to (`picker: "only"`), else null. */
export function featureSourceOnlyLayer(featureSource: FeatureSource | null | undefined): string | null {
  return featureSource?.picker === 'only' && featureSource.layer ? featureSource.layer : null;
}

function normalizeMapping(mapping: string | FeatureSourceFieldMapping | null | undefined): FeatureSourceFieldMapping | null {
  if (typeof mapping === 'string') return mapping.trim() ? { field: mapping } : null;
  if (mapping && typeof mapping === 'object' && typeof mapping.field === 'string' && mapping.field.trim()) {
    const values = mapping.values && typeof mapping.values === 'object' ? mapping.values : undefined;
    return { field: mapping.field, values };
  }
  return null;
}

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && !value.trim());
}

function comparable(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

/** Option value or label equal to `value` (case-insensitive, trimmed); returns the option's stored value. */
function matchOption(value: unknown, options: unknown[]): unknown {
  const wanted = comparable(value);
  for (const option of options) {
    if (option === null || option === undefined) continue;
    if (typeof option !== 'object') {
      if (comparable(option) === wanted) return option;
      continue;
    }
    const { value: stored, label } = option as { value?: unknown; label?: unknown };
    if ((stored !== undefined && stored !== null && comparable(stored) === wanted) || (label !== undefined && label !== null && comparable(label) === wanted)) {
      return stored ?? label;
    }
  }
  return undefined;
}

/** "09" and "9" are the same item number; "0" stays "0" and decimals keep their leading zero ("0.5"). */
function stripLeadingZeros(value: string): string {
  return value.replace(/^0+(?=\d)/, '');
}

function matchBidItem(value: unknown, bidItems: FeatureSourceBidItem[] | null | undefined): FeatureSourceBidItem | null {
  const wanted = comparable(value);
  if (!wanted) return null;
  const items = (bidItems || []).filter(bidItem => bidItem && !isBlank(bidItem.item) && bidItem.value !== undefined && bidItem.value !== null);
  return items.find(bidItem => comparable(bidItem.item) === wanted)
    || items.find(bidItem => stripLeadingZeros(comparable(bidItem.item)) === stripLeadingZeros(wanted))
    || null;
}
